<script>
import { putPerfil } from "@/api/ativacaoped/perfis.service";
import Modal from "@/components/g-modal.vue";
import Field from "@/components/input-v.vue";
import { toCurrency } from "@/helpers/toCurrency";
import { cloneDeep, isEqual } from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";

const cloneFormData = (value = {}) => {
  const { custos_medios, especialidade, senioridade, id } = value;

  return {
    id,
    custos_medios: custos_medios ?? [],
    especialidade: especialidade ?? "",
    senioridade: senioridade ?? ""
  };
};

export default {
  components: { Field, Modal },
  props: {
    opened: Boolean,
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: cloneFormData(this.value),
      originalValue: null,
      custoModalOpened: false,
      custoModalMode: null,
      custoFormData: {},
      custoEditingItem: null,
      mainOpened: this.opened,
      shouldNotify: false
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    headers() {
      return [
        {
          text: "Valor",
          value: "valor"
        },
        {
          text: "Competência inicial",
          value: "competencia_inicio"
        },
        {
          text: "Competência final",
          value: "competencia_fim"
        },
        {
          text: "",
          value: "actions",
          sortable: false
        }
      ];
    },
    fields() {
      return {
        senioridade: {
          key: "senioridade",
          name: "Senioridade",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }]
        },
        especialidade: {
          key: "especialidade",
          name: "Especialidade",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }]
        }
      };
    },
    editFields() {
      return {
        valor: {
          key: "valor",
          name: "Valor",
          type: this.$fieldTypes.MONEY,
          rules: [{ rule: "required" }]
        },
        competencia_inicio: {
          key: "competencia_inicio",
          name: "Competência inicial",
          type: this.$fieldTypes.MONTH,
          rules: [{ rule: "required" }]
        }
      };
    },
    custoMostrarCompetencia() {
      if (this.custoModalMode === "edit") {
        return !!this.custoEditingItem?.competencia_inicio;
      }

      return this.formData.custos_medios.length > 0;
    },
    custoHasChanges() {
      if (this.custoModalMode === "edit") {
        if (!this.custoEditingItem) return false;
        return !isEqual(this.custoFormData, this.custoEditingItem);
      }

      if (this.custoMostrarCompetencia) {
        return !!(
          this.custoFormData.valor || this.custoFormData.competencia_inicio
        );
      }

      return !!this.custoFormData.valor;
    },
    mainHasChanges() {
      if (!this.originalValue) return false;
      return (
        this.formData.senioridade !== this.originalValue.senioridade ||
        this.formData.especialidade !== this.originalValue.especialidade
      );
    },
    custoTitle() {
      const { especialidade, senioridade } = this.formData;
      const sufix =
        especialidade && senioridade
          ? `${senioridade} | ${especialidade}`
          : senioridade || especialidade;
      const prefix = this.custoModalMode === "edit" ? "Editar" : "Adicionar";

      return `${prefix} Custo Médio de ${sufix}`;
    }
  },
  watch: {
    formData: {
      deep: true,
      handler(nextValue) {
        const a = cloneDeep(nextValue);
        const b = cloneDeep(this.value);

        if (!isEqual(a, b)) {
          this.$emit("update:value", a);
        }
      }
    },
    value: {
      deep: true,
      handler(nextValue) {
        const a = cloneDeep(nextValue);
        const b = cloneDeep(this.formData);

        if (!isEqual(a, b)) {
          this.formData = cloneFormData(a);
        }
      }
    },
    opened(isOpen) {
      if (isOpen) {
        this.mainOpened = true;
        this.originalValue = cloneDeep(this.value);
      } else if (this.shouldNotify) {
        this.shouldNotify = false;
        this.$emit("saved");
      }
    }
  },
  methods: {
    openCustoModal(mode, item) {
      this.custoModalMode = mode;

      if (mode === "edit") {
        this.custoFormData = cloneDeep(item);
        this.custoEditingItem = item;
      } else {
        this.custoFormData = {};
        this.custoEditingItem = null;
      }

      this.mainOpened = false;
      this.custoModalOpened = true;
    },
    cancelCustoModal() {
      this.custoModalOpened = false;
      this.custoFormData = {};
      this.custoEditingItem = null;
      this.custoModalMode = null;
      this.mainOpened = true;
    },
    closeMainModal() {
      this.custoModalOpened = false;
      this.mainOpened = false;
      this.$emit("update:opened", false);
    },
    isFormValid(fields) {
      return Object.values(fields).every(field => {
        if (typeof field.valid === "function") {
          return field.valid() === true;
        }

        return true;
      });
    },
    async salvarCustoMedio() {
      const fieldsToValidate = { valor: this.editFields.valor };

      if (this.custoMostrarCompetencia) {
        fieldsToValidate.competencia_inicio = this.editFields.competencia_inicio;
      }

      if (!this.isFormValid(fieldsToValidate)) {
        return;
      }

      try {
        let result = null;
        const payload = cloneDeep(this.originalValue);

        if (this.custoModalMode === "edit") {
          const idx = payload.custos_medios.findIndex(
            ({ id }) => this.custoEditingItem.id === id
          );

          if (idx === -1) {
            this.notify(
              "Custo Médio não localizado para alteração. Tente novamente e caso o erro persista, contacte o suporte."
            );
            return;
          }

          payload.custos_medios.splice(idx, 1, cloneDeep(this.custoFormData));
          result = await putPerfil(this.clientId, payload.id, payload);
        } else {
          payload.custos_medios.push(cloneDeep(this.custoFormData));
          result = await putPerfil(this.clientId, payload.id, payload);
        }

        if (!result) {
          this.closeMainModal();
        }

        this.formData = cloneFormData(result);
        this.originalValue = cloneDeep(result);
        this.shouldNotify = true;
        this.custoModalOpened = false;
        this.custoFormData = {};
        this.custoEditingItem = null;
        this.custoModalMode = null;
        this.mainOpened = true;
      } catch (error) {
        this.notify(error);
      }
    },
    async salvarPerfil() {
      if (!this.isFormValid(this.fields)) {
        return;
      }

      try {
        const { especialidade, id, senioridade } = this.formData;
        await putPerfil(this.clientId, id, { especialidade, senioridade });

        this.shouldNotify = true;
        this.closeMainModal();
      } catch (error) {
        this.notify(error);
      }
    },
    toCurrency,
    toMonth(competencia) {
      return competencia
        ? moment(competencia, "YYYY-MM-DD").format("MM/YYYY")
        : "Indefinido";
    }
  }
};
</script>

<template>
  <div>
    <Modal
      appendTitleIcon="mdi-square-edit-outline"
      :opened.sync="mainOpened"
      title="Edição de Perfil"
      @close="closeMainModal"
    >
      <v-row>
        <v-col cols="6" class="pr-2">
          <Field
            v-model="formData.senioridade"
            :block.sync="fields.senioridade.block"
            :label="fields.senioridade"
            :valid.sync="fields.senioridade.valid"
          ></Field>
        </v-col>
        <v-col cols="6" class="pl-2">
          <Field
            v-model="formData.especialidade"
            :block.sync="fields.especialidade.block"
            :label="fields.especialidade"
            :valid.sync="fields.especialidade.valid"
          ></Field>
        </v-col>
      </v-row>
      <v-row justify="end" class="mr-1 mb-4">
        <v-btn color="var(--v-table-header-base)" depressed small @click="openCustoModal('add')">
          <v-icon x-small class="mr-1">mdi-plus</v-icon>
          <span class="text-caption">Adicionar</span>
        </v-btn>
      </v-row>
      <v-row>
        <v-data-table
          class="w-100 tabela-custos"
          disable-sort
          :headers="headers"
          hide-default-footer
          :items="formData.custos_medios"
        >
          <template v-slot:item.valor="{ item }">
            {{ toCurrency(item.valor) }}
          </template>
          <template v-slot:item.competencia_inicio="{ item }">
            {{ toMonth(item.competencia_inicio) }}
          </template>
          <template v-slot:item.competencia_fim="{ item }">
            {{ toMonth(item.competencia_fim) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="!item.competencia_fim"
              color="var(--v-table-header-base)"
              icon
              small
              @click="openCustoModal('edit', item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-row>

      <template #buttons="{ close }">
        <v-spacer />
        <v-btn class="px-5 ml-3" color="secondary" text @click="close">
          {{ shouldNotify ? "Fechar" : "Cancelar" }}
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          :dark="mainHasChanges"
          depressed
          :disabled="!mainHasChanges"
          @click="salvarPerfil"
        >
          Salvar
        </v-btn>
      </template>
    </Modal>

    <Modal
      :opened.sync="custoModalOpened"
      :title="custoTitle"
      @close="cancelCustoModal"
    >
      <v-row>
        <v-col
          :cols="custoMostrarCompetencia ? 6 : 12"
          :class="custoMostrarCompetencia ? 'pr-2' : ''"
        >
          <Field
            v-model="custoFormData.valor"
            :label="editFields.valor"
            :valid.sync="editFields.valor.valid"
          ></Field>
        </v-col>
        <v-col v-if="custoMostrarCompetencia" cols="6" class="pl-2">
          <Field
            v-model="custoFormData.competencia_inicio"
            :label="editFields.competencia_inicio"
            :valid.sync="editFields.competencia_inicio.valid"
          ></Field>
        </v-col>
      </v-row>

      <template #buttons>
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          text
          @click="cancelCustoModal"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          :dark="custoHasChanges"
          depressed
          :disabled="!custoHasChanges"
          @click="salvarCustoMedio"
        >
          Salvar
        </v-btn>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.tabela-custos ::v-deep {
  .v-data-table__wrapper {
    min-height: 192px;
  }

  th,
  td {
    height: 48px !important;

    &:nth-child(1) {
      text-align: right !important;
    }

    &:nth-child(2),
    &:nth-child(3) {
      text-align: center !important;
    }

    &:nth-child(4) {
      text-align: right !important;
    }
  }

  .v-data-table__empty-wrapper td {
    text-align: center !important;
  }
}
</style>
